import { __read, __rest, __assign } from 'tslib';
import * as React from 'react';
import { classNames } from 'uitk-react-utils';
import { throttle } from 'throttle-debounce';
import { getMonthYear } from '../generic-utils/dateUtils.esm.js';
import { UitkBaseCalendar } from '../CalendarBase/uitk-calendar.esm.js';
import styles from '../uitk-calendar.css';

var getClassNameList = classNames.bind(styles);
var getMaxNumMonths = function (month, year, maxDate) {
    // Get the maximum number of months to load, including start and end months
    var numMonths;
    var monthDate = new Date(year, month, 1);
    numMonths = (maxDate.getFullYear() - monthDate.getFullYear()) * 12;
    numMonths -= monthDate.getMonth();
    numMonths += maxDate.getMonth() + 1;
    return numMonths <= 0 ? 0 : numMonths;
};
var getInitialNumMonths = function (month, year, selectionStart, numMonths) {
    var selected = selectionStart || '';
    if (selected) {
        var selectedMonth = selected.split('-')[1];
        var selectedYear = selected.split('-')[0];
        var selDate = new Date(Number(selectedYear), Number(selectedMonth), 1);
        var monthDate = new Date(year, month, 1);
        // In certain circumstances this can return a negative number.
        //  1) selection date is in the past
        //  2) consumer is hardwiring the month/year (if not, all is well)
        //  3) has to be in mobile view with mode to "scrolling"
        // It is not immediately obvious what to do in these situations, and since we
        // do not have a use case that fits the scenario above, we'll punt on it for now.
        return selDate.getMonth() - monthDate.getMonth() + 12 * (selDate.getFullYear() - monthDate.getFullYear()) + 1;
    }
    return numMonths;
};
var UitkCalendarNavigationScrolling = function (props) {
    var elem = React.useRef(null);
    var _a = getMonthYear(), month = _a.month, year = _a.year;
    var selectionStart = props.selectionStart, numMonthsToDisplay = props.numMonthsToDisplay, maxDate = props.maxDate;
    var _b = __read(React.useState(getInitialNumMonths(month, year, selectionStart, 
    // We need atleast 4 months initially loaded to ensure
    // a scroll overflow is created to enable infinite scroll
    numMonthsToDisplay && numMonthsToDisplay > 4 ? numMonthsToDisplay : 4)), 2), loadedMonths = _b[0], setLoadedMonths = _b[1];
    var maxLoadableMonths = maxDate ? getMaxNumMonths(month, year, maxDate) : null;
    React.useEffect(function () {
        var scrollParent;
        // Throttled call that loads new months if we approach the end of the list
        var lazyLoadHandler = throttle(80, function () {
            var _a;
            var elemBottom = (_a = elem.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().bottom;
            var viewPortHeight = scrollParent.clientHeight || scrollParent.innerHeight;
            var diffBuffer = viewPortHeight * 0.8;
            if (elemBottom && elemBottom - diffBuffer <= viewPortHeight) {
                if (loadedMonths < maxLoadableMonths || maxLoadableMonths === null) {
                    setLoadedMonths(loadedMonths + 1);
                }
            }
        });
        // Utility to get the scrolling element. We can't guarantee that the window
        // is what is scrolling. The datepickers, for example, scroll within a div.
        var getScrollParent = function (element) {
            var style = window.getComputedStyle(element);
            var excludeStaticParent = style.position === 'absolute';
            var overflowRegex = /(auto|scroll)/;
            if (style.position === 'fixed') {
                return window;
            }
            while (element) {
                style = window.getComputedStyle(element);
                if (excludeStaticParent && style.position === 'static') {
                    // eslint-disable-next-line no-continue
                    continue;
                }
                if (overflowRegex.test(style.overflow + style.overflowY) && element.tagName !== 'HTML') {
                    return element;
                }
                // eslint-disable-next-line no-param-reassign
                element = element.parentElement;
            }
            return window;
        };
        // Initialize what is needed for infinite scrolling.
        var setupInfiniteScrolling = function () {
            try {
                // Determine what element (or window) scrolls
                scrollParent = getScrollParent(elem.current);
                if (elem.current && elem.current === scrollParent) {
                    elem.current = elem.current.firstChild;
                }
                // Listen for scroll events
                scrollParent.addEventListener('scroll', lazyLoadHandler);
                // Populate initially so that num months shown fill the height of the screen
                lazyLoadHandler();
            }
            catch (e) {
                // Used to catch errors during unit testing. Should not happen
                // in browser.
            }
        };
        setupInfiniteScrolling();
        // Unmount Listener
        return function () { return scrollParent.removeEventListener('scroll', lazyLoadHandler); };
    }, [loadedMonths, maxLoadableMonths]);
    var className = props.className, rest = __rest(props, ["className"]);
    var rootClassNames = getClassNameList(className);
    var numColumns = 1;
    return (React.createElement("div", { className: rootClassNames, ref: elem, "data-testid": "calendar-scrolling" },
        React.createElement(UitkBaseCalendar, __assign({}, rest, { month: month, numColumns: numColumns, numMonthsToDisplay: loadedMonths, padWeeks: true, year: year, scrollToMonth: true }))));
};

export { UitkCalendarNavigationScrolling };
